import { IAppReducer } from 'models';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCardsByUserId } from 'shared/services/payment-method';
import { getPaymentMethodLink } from 'shared/services/payment-method-link';
import { CreditCard } from '../types/Add-subscription.types';

interface UsePaymentMethodReturn {
  paymentLink: string | null;
  isPaymentLinkModalOpen: boolean;
  isGeneratingLink: boolean;
  generatePaymentLink: () => void;
  handleRefreshCards: () => void;
  isLoading: boolean;
  error: string | null;
  selectedMethod: string | null;
  handleMethodChange: (value: string) => void;
  handleCardSelection: (cardId: string) => void;
  creditCards: CreditCard[];
  showNewCardForm: boolean;
  selectedCard: string | null;
  handleShowNewCardForm: (value: boolean) => void;
  handleIsPaymentLinkModalOpen: (value: boolean) => void;
  handleError: (value: string | null) => void;
}

interface UsePaymentMethodProps {
  onSelectPaymentMethod: (method: string | null) => void;
}

export const usePaymentMethod = ({
  onSelectPaymentMethod,
}: UsePaymentMethodProps): UsePaymentMethodReturn => {
  const { authReducer: user } = useSelector((state: IAppReducer) => state);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [creditCards, setCreditCards] = useState<CreditCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentLinkModalOpen, setIsPaymentLinkModalOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const fetchCreditCards = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const cards = await getCardsByUserId(user.uid);
      setCreditCards(cards);
    } catch (err) {
      console.log(err);
      setError('Error al cargar las tarjetas. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCreditCards();
  }, []);

  const handleShowNewCardForm = (value: boolean) => {
    setShowNewCardForm(value);
  };

  const handleIsPaymentLinkModalOpen = (value: boolean) => {
    setIsPaymentLinkModalOpen(value);
  };

  const handleError = (value: string | null) => {
    setError(value);
  };

  const handleMethodChange = (value: string) => {
    setSelectedMethod(value);
    if (value === 'credit_card') {
      const principalCard = creditCards.find((card) => card.is_principal);
      if (principalCard) {
        setSelectedCard(principalCard.id);
        onSelectPaymentMethod(principalCard.id);
      } else {
        setSelectedCard(null);
        onSelectPaymentMethod(null);
      }
    } else {
      setSelectedCard(null);
      onSelectPaymentMethod(null);
    }
    setShowNewCardForm(false);
  };

  const handleCardSelection = (cardId: string) => {
    setSelectedCard(cardId);
    onSelectPaymentMethod(cardId);
    setShowNewCardForm(false);
  };

  const generatePaymentLink = async () => {
    setIsGeneratingLink(true);
    try {
      const paymentMethodLink = await getPaymentMethodLink(user.uid);
      setPaymentLink(paymentMethodLink.url);
      setIsPaymentLinkModalOpen(true);
    } catch (error) {
      setError('Error al generar el enlace de pago');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleRefreshCards = async () => {
    handleShowNewCardForm(false);
    setIsLoading(true);
    setError(null);
    try {
      const cards = await getCardsByUserId(user.uid);
      setCreditCards(cards);
    } catch (err) {
      console.error(err);
      setError(
        'Error al actualizar las tarjetas. Por favor, intente de nuevo.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    paymentLink,
    isPaymentLinkModalOpen,
    isGeneratingLink,
    generatePaymentLink,
    handleRefreshCards,
    isLoading,
    error,
    selectedMethod,
    handleMethodChange,
    handleCardSelection,
    creditCards,
    showNewCardForm,
    selectedCard,
    handleShowNewCardForm,
    handleIsPaymentLinkModalOpen,
    handleError,
  };
};
